import { useId, useState } from 'react';

import { isLiabilityAccountType, type EntryType } from '@pfo/domain';

import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import { MoneyInput } from '../../../shared/ui/MoneyInput.js';
import { todayInputValue } from '../../../shared/lib/dates.js';
import type { QuickAddOptions } from '../../transactions/data/quickAddRepository.js';
import {
  createTemplate,
  replaceRecurringTemplate,
  type AmountMode,
  type RecurringFrequency,
  type RecurringTemplate,
  type RecurringTemplateLine,
} from '../data/recurringRepository.js';
import { AMOUNT_MODE_LABELS, FREQUENCY_LABELS } from '../model/recurringLabels.js';

/** The three shapes a household actually schedules. A salary split is saved from the entry. */
const KINDS: ReadonlyArray<
  readonly [Extract<EntryType, 'expense' | 'income' | 'transfer'>, string]
> = [
  ['expense', 'Khoản chi'],
  ['income', 'Khoản thu'],
  ['transfer', 'Chuyển tiền'],
];

/**
 * Creating a recurring template: what it is, how much, how often, from when.
 *
 * The ledger lines are built here from the same choices Quick Add offers, so a template
 * produces exactly the entry the user would have typed by hand — `app.confirm_recurring`
 * copies these lines verbatim.
 *
 * "Số tiền thay đổi" (`estimated`) is the electricity case: the shape is known, the amount is
 * not. Such a template stores no amount and cannot be confirmed without one.
 *
 * With `initial` the same form EDITS a template (plan R3, decision AE3). The kind is locked
 * there: changing what sort of entry a template produces is a different template, not an edit,
 * and the RPC refuses it — history has to stay readable against the thing that produced it.
 * The edit takes effect from the next unconfirmed occurrence; entries already posted keep the
 * numbers they were posted with.
 */
export function TemplateForm({
  options,
  householdId,
  subjectId,
  timeZone,
  initial,
  onCreated,
  onCancel,
}: {
  options: QuickAddOptions;
  householdId: string;
  subjectId: string;
  timeZone: string;
  /** Present = edit that template instead of creating a new one. */
  initial?: RecurringTemplate;
  onCreated: (name: string) => void;
  onCancel: () => void;
}) {
  const nameId = useId();
  const kindId = useId();
  const amountId = useId();
  const categoryId = useId();
  const accountId = useId();
  const sourceId = useId();
  const toAccountId = useId();
  const frequencyId = useId();
  const dayId = useId();
  const startId = useId();

  const assetAccounts = options.accounts.filter(
    (account) => !isLiabilityAccountType(account.accountType),
  );

  const isEdit = initial !== undefined;
  // Lines store LEDGER accounts; the form speaks in the accounts a person sees, so an edit has
  // to walk the mapping backwards.
  const accountOfLedger = (ledgerAccountId: string | undefined) =>
    options.accounts.find((item) => item.ledgerAccountId === ledgerAccountId)?.id ?? '';
  const initialDebit = initial?.lines.find((line) => line.side === 'debit');
  const initialCredit = initial?.lines.find((line) => line.side === 'credit');
  // A template can be a split or a debt payment too; this form only speaks the three kinds it
  // can build, so anything else falls back to expense and keeps the locked selector honest.
  const initialKind: string = initial?.entryType ?? 'expense';

  const [name, setName] = useState(initial?.name ?? '');
  const [kind, setKind] = useState<'expense' | 'income' | 'transfer'>(
    initialKind === 'income' || initialKind === 'transfer' ? initialKind : 'expense',
  );
  const [amountMode, setAmountMode] = useState<AmountMode>(initial?.amountMode ?? 'fixed');
  const [amount, setAmount] = useState<number | null>(initialDebit?.amount ?? null);
  const [category, setCategory] = useState(
    initialDebit?.categoryId ?? options.categories[0]?.id ?? '',
  );
  const [account, setAccount] = useState(() => {
    if (initial === undefined) return options.defaultAccountId ?? assetAccounts[0]?.id ?? '';
    // Expense/transfer pay FROM the credit leg; income arrives INTO the debit leg.
    return initialKind === 'income'
      ? accountOfLedger(initialDebit?.ledgerAccountId)
      : accountOfLedger(initialCredit?.ledgerAccountId);
  });
  const [source, setSource] = useState(
    () =>
      options.incomeSources.find((item) => item.ledgerAccountId === initialCredit?.ledgerAccountId)
        ?.id ??
      options.incomeSources[0]?.id ??
      '',
  );
  const [destination, setDestination] = useState(() =>
    initialKind === 'transfer' ? accountOfLedger(initialDebit?.ledgerAccountId) : '',
  );
  const [frequency, setFrequency] = useState<RecurringFrequency>(initial?.frequency ?? 'monthly');
  const [startDate, setStartDate] = useState(
    () => initial?.nextDueDate ?? todayInputValue(timeZone),
  );

  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  // The day of the month comes from the start date rather than a field of its own: "starts on
  // the 5th" and "due on the 5th" are the same fact, and asking twice invites them to differ.
  const dayOfMonth = frequency === 'weekly' ? null : Number(startDate.slice(8, 10));

  const needsAmount = amountMode === 'fixed';
  const targetChosen =
    kind === 'expense'
      ? category !== '' && account !== ''
      : kind === 'income'
        ? source !== '' && account !== ''
        : account !== '' && destination !== '' && account !== destination;
  const canSave =
    name.trim() !== '' && targetChosen && (!needsAmount || (amount !== null && amount > 0));

  function buildLines(): readonly RecurringTemplateLine[] {
    // Stored amounts are the SHAPE. For an estimated template they are null and the confirmed
    // amount fills them in; for a fixed one they are the amount itself.
    const value = amountMode === 'estimated' ? null : amount;
    const ledgerOf = (id: string) =>
      options.accounts.find((item) => item.id === id)?.ledgerAccountId ?? '';

    if (kind === 'expense') {
      return [
        {
          ledgerAccountId: options.expensesLedgerAccountId,
          side: 'debit',
          amount: value,
          categoryId: category,
          planningNature: 'fixed_recurring',
          sortOrder: 0,
        },
        {
          ledgerAccountId: ledgerOf(account),
          side: 'credit',
          amount: value,
          categoryId: null,
          planningNature: null,
          sortOrder: 1,
        },
      ];
    }

    if (kind === 'income') {
      const incomeSource = options.incomeSources.find((item) => item.id === source);
      return [
        {
          ledgerAccountId: ledgerOf(account),
          side: 'debit',
          amount: value,
          categoryId: null,
          planningNature: null,
          sortOrder: 0,
        },
        {
          ledgerAccountId: incomeSource?.ledgerAccountId ?? '',
          side: 'credit',
          amount: value,
          categoryId: null,
          planningNature: null,
          sortOrder: 1,
        },
      ];
    }

    return [
      {
        ledgerAccountId: ledgerOf(destination),
        side: 'debit',
        amount: value,
        categoryId: null,
        planningNature: null,
        sortOrder: 0,
      },
      {
        ledgerAccountId: ledgerOf(account),
        side: 'credit',
        amount: value,
        categoryId: null,
        planningNature: null,
        sortOrder: 1,
      },
    ];
  }

  async function handleSubmit() {
    if (!canSave) return;
    setSaving(true);
    setMessage(null);
    try {
      if (initial !== undefined) {
        await replaceRecurringTemplate({
          templateId: initial.id,
          patch: {
            name: name.trim(),
            amountMode,
            frequency,
            dayOfMonth,
            // "Kỳ đầu tiên" edits the NEXT occurrence here: the template already started, and
            // its start date is history.
            nextDueDate: startDate,
          },
          lines: buildLines(),
        });
        onCreated(name.trim());
        return;
      }

      await createTemplate({
        householdId,
        subjectId,
        name: name.trim(),
        entryType: kind,
        amountMode,
        frequency,
        dayOfMonth,
        startDate,
        // The first occurrence IS the start date; everything after it comes from
        // app.next_due_date, so the rule lives in exactly one place.
        nextDueDate: startDate,
        lines: buildLines(),
      });
      onCreated(name.trim());
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className={forms.form}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <div className={forms.field}>
        <label htmlFor={nameId}>Tên khoản</label>
        <input
          id={nameId}
          className={forms.input}
          type="text"
          value={name}
          placeholder="Ví dụ: Internet"
          autoComplete="off"
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <div className={forms.field}>
        <label htmlFor={kindId}>Loại</label>
        <select
          id={kindId}
          className={forms.input}
          value={kind}
          disabled={isEdit}
          onChange={(event) => setKind(event.target.value as typeof kind)}
        >
          {KINDS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {isEdit ? (
          <p className={forms.hint}>
            Không đổi được loại khi sửa — muốn đổi thì tạo khoản mới rồi tắt khoản này.
          </p>
        ) : null}
      </div>

      {kind === 'expense' ? (
        <div className={forms.field}>
          <label htmlFor={categoryId}>Danh mục</label>
          <select
            id={categoryId}
            className={forms.input}
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            {options.categories.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {kind === 'income' ? (
        <div className={forms.field}>
          <label htmlFor={sourceId}>Nguồn thu</label>
          <select
            id={sourceId}
            className={forms.input}
            value={source}
            onChange={(event) => setSource(event.target.value)}
          >
            {options.incomeSources.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className={forms.field}>
        <label htmlFor={accountId}>{kind === 'income' ? 'Tiền về tài khoản' : 'Trả từ'}</label>
        <select
          id={accountId}
          className={forms.input}
          value={account}
          onChange={(event) => setAccount(event.target.value)}
        >
          {assetAccounts.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>

      {kind === 'transfer' ? (
        <div className={forms.field}>
          <label htmlFor={toAccountId}>Đến tài khoản</label>
          <select
            id={toAccountId}
            className={forms.input}
            value={destination}
            onChange={(event) => setDestination(event.target.value)}
          >
            <option value="">Chọn…</option>
            {assetAccounts
              .filter((item) => item.id !== account)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </div>
      ) : null}

      <fieldset className={forms.field}>
        <legend>Số tiền</legend>
        {(['fixed', 'estimated'] as const).map((mode) => (
          <label key={mode} className={forms.hint}>
            <input
              type="radio"
              name="amount-mode"
              value={mode}
              checked={amountMode === mode}
              onChange={() => setAmountMode(mode)}
            />{' '}
            {AMOUNT_MODE_LABELS[mode]}
          </label>
        ))}
        <p className={forms.hint}>
          Chọn “số tiền thay đổi” cho điện, nước… — mỗi lần xác nhận sẽ nhập số thật trên hoá đơn.
        </p>
      </fieldset>

      {needsAmount ? (
        <div className={forms.field}>
          <label htmlFor={amountId}>Số tiền mỗi kỳ</label>
          <MoneyInput id={amountId} value={amount} onChange={setAmount} />
        </div>
      ) : null}

      <div className={forms.field}>
        <label htmlFor={frequencyId}>Tần suất</label>
        <select
          id={frequencyId}
          className={forms.input}
          value={frequency}
          onChange={(event) => setFrequency(event.target.value as RecurringFrequency)}
        >
          {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className={forms.field}>
        <label htmlFor={startId}>{isEdit ? 'Kỳ kế tiếp' : 'Kỳ đầu tiên'}</label>
        <input
          id={startId}
          className={forms.input}
          type="date"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
        />
        {dayOfMonth !== null ? (
          <p className={forms.hint} id={dayId}>
            Các kỳ sau rơi vào ngày {dayOfMonth} — tháng nào không có ngày đó thì lùi về ngày cuối
            tháng.
          </p>
        ) : null}
      </div>

      {isEdit ? (
        <p className={forms.hint}>
          Thay đổi áp dụng từ kỳ chưa ghi kế tiếp. Giao dịch đã ghi giữ nguyên số cũ. Nếu đây là
          khoản góp quỹ dự phòng, nhịp góp trong chính sách quỹ không tự đổi theo.
        </p>
      ) : null}

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      <div className={forms.actions}>
        <button type="button" className={forms.secondary} onClick={onCancel}>
          Hủy
        </button>
        <button type="submit" className={forms.primary} disabled={!canSave || isSaving}>
          {isSaving ? 'Đang lưu…' : isEdit ? 'Lưu thay đổi' : 'Lưu khoản định kỳ'}
        </button>
      </div>
    </form>
  );
}
